import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import usersService from "../../../../services/users.service";
import { useEyeconxStore } from "../../../../store/eyeconxStore";
import Loading from "../../../common/Loading";
import UserReminder from "./UserReminder";

const UserCard = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [groupedUsers, setGroupedUsers] = useState({});
    const [expandedRoles, setExpandedRoles] = useState({
        ADMIN: true,
        MANAGER: true,
        ECP: true,
        STAFF: true,
        OTHER: true,
    });
    const [showReminder, setShowReminder] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [localError, setLocalError] = useState("");
    const [localSuccess, setLocalSuccess] = useState("");

    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await usersService.getUsers(clinicId);
            setUsers(response.data);
            const grouped = response.data.reduce((acc, user) => {
                if (!acc[user.role]) acc[user.role] = [];
                acc[user.role].push(user);
                return acc;
            }, {});
            const roleOrder = ["ADMIN", "MANAGER", "ECP", "STAFF", "OTHER"];
            const sorted = {};
            roleOrder.forEach((r) => grouped[r] && (sorted[r] = grouped[r]));
            Object.keys(grouped).forEach(
                (r) => !roleOrder.includes(r) && (sorted[r] = grouped[r])
            );
            setGroupedUsers(sorted);
        } catch (err) {
            setError("Failed to fetch users");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        setEditingId(user.id);
        setSelectedUser(user);
        setLocalError("");
        setLocalSuccess("");
        // Set form values for editing
        Object.keys(user).forEach((key) => setValue(key, user[key]));
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setSelectedUser(null);
        setLocalError("");
        setLocalSuccess("");
        reset();
    };

    const onSubmit = async (formData) => {
        try {
            setLoading(true);
            setLocalError("");
            await usersService.updateUser(clinicId, editingId, formData);
            setLocalSuccess("User updated successfully!");

            setTimeout(() => {
                setEditingId(null);
                setSelectedUser(null);
                fetchUsers();
                setLocalSuccess("");
            }, 1500);
        } catch (err) {
            setLocalError("Failed to update user.");
        } finally {
            setLoading(false);
        }
    };

    const handleUserReminder = (user) => {
        setSelectedUser(user);
        setShowReminder(true);
    };

    const toggleRoleSection = (role) =>
        setExpandedRoles((prev) => ({ ...prev, [role]: !prev[role] }));

    const getRoleDisplayName = (role) => {
        switch (role) {
            case "ADMIN":
                return "Administrators";
            case "MANAGER":
                return "Managers";
            case "ECP":
                return "Eye Care Professionals";
            case "STAFF":
                return "Staff Members";
            case "OTHER":
                return "Other Roles";
            default:
                return `${role}s`;
        }
    };

    const getStatusBadge = (user) => {
        const badges = [];

        // Blocked badge
        if (user.isBlocked) {
            badges.push(
                <span key="blocked" className="badge bg-danger text-white me-1">
                <i className="bi bi-slash-circle me-1"></i>
                Blocked
            </span>
            );
        }

        // Active/Inactive badge
        if (user.isActive) {
            badges.push(
                <span key="active" className="badge bg-success text-white me-1">
                <i className="bi bi-check-circle me-1"></i>
                Active
            </span>
            );
        } else {
            badges.push(
                <span key="inactive" className="badge bg-secondary text-white me-1">
                <i className="bi bi-pause-circle me-1"></i>
                Inactive
            </span>
            );
        }

        // Authentication status badge
        if (user.isAuthenticated) {
            badges.push(
                <span key="authenticated" className="badge bg-primary text-white me-1">
                <i className="bi bi-shield-check me-1"></i>
                Authenticated
            </span>
            );
        } else {
            badges.push(
                <span key="pending" className="badge bg-warning text-dark me-1">
                <i className="bi bi-clock me-1"></i>
                Pending Auth
            </span>
            );
        }

        return <>{badges}</>;
    };


    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const UserRowEdit = ({ user, onSave, onCancel }) => (
        <tr className="border-top bg-light">
            <td colSpan="7" className="p-0">
                <div className="p-3 border-bottom">
                    {localError && <div className="alert alert-danger mb-3">{localError}</div>}
                    {localSuccess && <div className="alert alert-success mb-3">{localSuccess}</div>}

                    <form onSubmit={handleSubmit(onSave)}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold">First Name</label>
                                <input
                                    type="text"
                                    className={`form-control form-control-sm ${errors.firstName ? 'is-invalid' : ''}`}
                                    {...register("firstName", { required: 'First name is required' })}
                                />
                                {errors.firstName && <div className="invalid-feedback">{errors.firstName.message}</div>}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold">Last Name</label>
                                <input
                                    type="text"
                                    className={`form-control form-control-sm ${errors.lastName ? 'is-invalid' : ''}`}
                                    {...register("lastName", { required: 'Last name is required' })}
                                />
                                {errors.lastName && <div className="invalid-feedback">{errors.lastName.message}</div>}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold">Username</label>
                                <input
                                    type="text"
                                    className={`form-control form-control-sm ${errors.username ? 'is-invalid' : ''}`}
                                    {...register("username", { required: 'Username is required' })}
                                />
                                {errors.username && <div className="invalid-feedback">{errors.username.message}</div>}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold">Email</label>
                                <input
                                    type="email"
                                    className={`form-control form-control-sm ${errors.email ? 'is-invalid' : ''}`}
                                    {...register("email", {
                                        required: 'Email is required',
                                        pattern: {
                                            value: /^\S+@\S+$/i,
                                            message: 'Invalid email address'
                                        }
                                    })}
                                />
                                {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                            </div>

                            <div className="col-12">
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                disabled
                                                {...register("isActive")}
                                            />
                                            <label className="form-check-label small">Active</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                disabled
                                                {...register("isBlocked")}
                                            />
                                            <label className="form-check-label small">Blocked</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                disabled
                                                {...register("isAuthenticated")}
                                            />
                                            <label className="form-check-label small">Authenticated</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="btn-group">
                                    <button type="submit" className="btn btn-success text-white btn-sm" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check me-1"></i> Save
                                            </>
                                        )}
                                    </button>
                                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
                                        <i className="bi bi-x me-1"></i> Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </td>
        </tr>
    );

    return (
        <>
            {loading && !editingId && <Loading />}



            {loading && !editingId ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading users...</p>
                </div>
            ) : users.length === 0 ? (
                <div className="text-center py-5">
                    <i className="bi bi-people display-1 text-muted"></i>
                    <h5 className="text-muted mt-3">No users found</h5>
                    <p className="text-muted">Get started by adding your first user</p>
                </div>
            ) : (
                <div className="card bg-light shadow-sm border-0 h-100">
                    <div className="card-body">
                        {Object.entries(groupedUsers).map(([role, roleUsers]) => (
                            <div className="role-section mb-4 border rounded" key={role}>
                                <div
                                    className="role-header d-flex justify-content-between align-items-center p-3 rounded-top cursor-pointer"
                                    onClick={() => toggleRoleSection(role)}
                                >
                                    <div className="d-flex align-items-center">
                                        <div className="mb-0 me-2 fw-semibold">{getRoleDisplayName(role)}</div>
                                        <span className="badge bg-primary">{roleUsers.length}</span>
                                    </div>
                                    <i
                                        className={`bi ${
                                            expandedRoles[role]
                                                ? "bi-chevron-down"
                                                : "bi-chevron-right"
                                        }`}
                                    ></i>
                                </div>

                                {expandedRoles[role] && (
                                    <div className="role-content">
                                        <div className="table-responsive">
                                            <table className="table table-hover align-middle table-striped">
                                                <thead>
                                                <tr>
                                                    <th scope="col">Status</th>
                                                    <th scope="col" className="ps-4">
                                                        <i className="bi bi-person me-2"></i> User Details
                                                    </th>
                                                    <th scope="col">User Number</th>
                                                    <th scope="col">Username</th>

                                                    <th scope="col" className="text-end pe-4">Actions</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {roleUsers.map((user) => (
                                                    <React.Fragment key={user.id}>
                                                        <tr className="border-top">
                                                            <td>
                                                                {getStatusBadge(user)}
                                                            </td>
                                                            <td className="ps-4">
                                                                <div className="d-flex align-items-center">
                                                                    <div className="avatar avatar-sm p-2 bg-primary text-white rounded-circle me-3">
                                                                        {user.firstName?.charAt(0) || 'U'}
                                                                        {user.lastName?.charAt(0) || ''}
                                                                    </div>
                                                                    <div>
                                                                        <h6 className="mb-0 fw-semibold">
                                                                            {user.firstName} {user.lastName}
                                                                        </h6>
                                                                        <small className="text-muted">
                                                                            {user.email || 'No email provided'}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <code>{user.number}</code>
                                                            </td>
                                                            <td>
                                                                <code>{user.username}</code>
                                                            </td>

                                                            <td className="text-end pe-4">
                                                                <div className="btn-group">
                                                                    {user?.role === "ECP" && (
                                                                        <button
                                                                            className="btn btn-outline-info btn-sm"
                                                                            onClick={() => handleUserReminder(user)}
                                                                            title="Send Reminder"
                                                                        >
                                                                            <i className="bi bi-clock"></i>
                                                                        </button>
                                                                    )}

                                                                    <button
                                                                        className="btn btn-outline-primary btn-sm"
                                                                        onClick={() => handleEdit(user)}
                                                                        title="Edit User"
                                                                    >
                                                                        <i className="bi bi-pencil"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        {editingId === user.id && (
                                                            <UserRowEdit
                                                                user={user}
                                                                onSave={onSubmit}
                                                                onCancel={handleCancelEdit}
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <UserReminder
                show={showReminder}
                onClose={() => setShowReminder(false)}
                user={selectedUser}
            />
        </>
    );
};

export default UserCard;